import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AppHeader from '../../../components/AppHeader.tsx';
import EmployeeModal from '../../../components/EmployeeModal.tsx';
import { useAuthentication } from '@/features/auth/useAuthentication';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faDownload, faEdit, faEye, faSearch, faTrash, faUserPlus, faUsers, faUserTie } from '@fortawesome/free-solid-svg-icons';
import { useEmployeeStore } from '../../../features/services/store/useEmployeeStore.ts';
import { useSalonStore } from '../../../features/salons/store/useSalonStore.ts';
import type { CreateEmployeeRequest, EmployeeResponse, UpdateEmployeeRequest } from '../../../api/generated';

const StaffPage: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const { canManageStaff } = useAuthentication();
  const allowManage = canManageStaff(entityId);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingEmployee, setEditingEmployee] = useState<EmployeeResponse | null>(null);

  // Stores
  const {
    employees,
    loading: employeesLoading,
    error: employeesError,
    loadEmployees,
    createEmployee,
    updateEmployee,
    deleteEmployee,
  } = useEmployeeStore();

  const { salons, loadSalons } = useSalonStore();

  const breadcrumb = [{ label: 'Zaměstnanci' }];

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadEmployees(), loadSalons()]);
    };
    loadData();
  }, [loadEmployees, loadSalons]);

  const filteredEmployees = employees.filter(employee => {
    const fullName = `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase()) || (employee.email ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || employee.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Employee management functions
  const handleOpenCreateModal = () => {
    setModalMode('create');
    setEditingEmployee(null);
    setShowEmployeeModal(true);
  };

  const handleOpenEditModal = (employee: EmployeeResponse) => {
    setModalMode('edit');
    setEditingEmployee(employee);
    setShowEmployeeModal(true);
  };

  const handleCloseModal = () => {
    setShowEmployeeModal(false);
    setEditingEmployee(null);
  };

  const handleSaveEmployee = async (data: CreateEmployeeRequest | UpdateEmployeeRequest, employeeId?: string) => {
    try {
      if (modalMode === 'create') {
        await createEmployee(data as CreateEmployeeRequest);
      } else if (modalMode === 'edit' && employeeId) {
        await updateEmployee(employeeId, data as UpdateEmployeeRequest);
      }

      // Reload employees to show updated data
      await loadEmployees();
      handleCloseModal();
    } catch (error) {
      console.error('Chyba při ukládání zaměstnance:', error);
      throw error;
    }
  };

  const handleDeleteEmployee = async (id: string) => {
    if (window.confirm('Opravdu chcete smazat tohoto zaměstnance?')) {
      try {
        await deleteEmployee(id);
        // Explicitly reload employees to ensure fresh data
        await loadEmployees();
      } catch (error) {
        console.error('Chyba při mazání zaměstnance:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    if (!status) return 'Neznámý';
    switch (status.toLowerCase()) {
      case 'active':
        return 'Aktivní';
      case 'inactive':
        return 'Neaktivní';
      default:
        return status;
    }
  };

  const getRoleText = (role: string) => {
    if (!role) return 'Neznámá role';
    switch (role.toUpperCase()) {
      case 'OWNER':
        return 'Vlastník';
      case 'RECEPTION':
        return 'Recepce';
      case 'WORKER':
        return 'Pracovník';
      default:
        return role;
    }
  };

  // Loading state
  const isLoading = employeesLoading;
  const hasError = employeesError;

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Zaměstnanci" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Zaměstnanci</h1>
                <p className="text-gray-600 mt-1">Spravujte tým zaměstnanců</p>
                {/* Statistics */}
                <div className="flex items-center space-x-4 mt-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">
                      Aktivní: <span className="font-medium text-gray-900">{employees.filter(emp => emp.status === 'ACTIVE').length}</span>
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <span className="text-sm text-gray-600">
                      Celkem: <span className="font-medium text-gray-900">{employees.length}</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3">
                <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
                {allowManage && (
                  <button
                    onClick={handleOpenCreateModal}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                    Přidat zaměstnance
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* Error State */}
          {hasError && (
            <section className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-red-600">
                <p className="font-medium">Chyba při načítání zaměstnanců:</p>
                <p className="text-sm mt-1">{hasError}</p>
              </div>
            </section>
          )}

          {/* Loading State */}
          {isLoading && (
            <section className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-center py-8">
                <div className="text-gray-500">Načítání zaměstnanců...</div>
              </div>
            </section>
          )}

          {/* Search and Filters */}
          {!isLoading && !hasError && (
            <section className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center space-x-4">
                <div className="flex-1 relative">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Hledat zaměstnance..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Všechny stavy</option>
                    <option value="ACTIVE">Aktivní</option>
                    <option value="INACTIVE">Neaktivní</option>
                  </select>
                  <FontAwesomeIcon icon={faChevronDown} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </section>
          )}

          {/* Employee Cards */}
          {!isLoading && !hasError && (
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEmployees.map(employee => (
                <div key={employee.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  {/* Employee Header */}
                  <div className="flex items-center mb-4">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mr-4">
                      <FontAwesomeIcon icon={faUserTie} className="text-gray-500 text-xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {employee.firstName} {employee.lastName}
                      </h3>
                      <p className="text-sm text-gray-500">{getRoleText(employee.role)}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(employee.status)}`}>{getStatusText(employee.status)}</span>
                  </div>

                  {/* Employee Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Email</span>
                      <span className="font-medium text-gray-900 truncate max-w-[60%]" title={employee.email}>
                        {employee.email}
                      </span>
                    </div>
                    {employee.phoneNumber && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Telefon</span>
                        <span className="font-medium text-gray-900">{employee.phoneNumber}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Role</span>
                      <span className="font-medium text-gray-900">{getRoleText(employee.role)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-2">
                    <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Zobrazit detaily">
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    {allowManage && (
                      <button onClick={() => handleOpenEditModal(employee)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Upravit">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    )}
                    {allowManage && (
                      <button onClick={() => handleDeleteEmployee(employee.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Smazat">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </section>
          )}

          {/* Empty State */}
          {!isLoading && !hasError && filteredEmployees.length === 0 && (
            <section className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-center py-12">
                <FontAwesomeIcon icon={faUsers} className="text-gray-300 text-6xl mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Žádní zaměstnanci</h3>
                <p className="text-gray-500 mb-4">
                  {searchQuery || statusFilter
                    ? 'Nebyli nalezeni žádní zaměstnanci odpovídající vašim kritériím.'
                    : 'Zatím nemáte žádné zaměstnance. Přidejte prvního zaměstnance.'}
                </p>
                {!searchQuery && !statusFilter && (
                  <button
                    onClick={handleOpenCreateModal}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                    Přidat prvního zaměstnance
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Employee Modal */}
          <EmployeeModal
            isOpen={showEmployeeModal}
            onClose={handleCloseModal}
            mode={modalMode}
            employee={editingEmployee}
            salons={salons}
            onSave={handleSaveEmployee}
          />
        </div>
      </div>
    </div>
  );
};

export default StaffPage;
